const TicksPerMillisecond = 1;
const TicksPerSecond = TicksPerMillisecond * 1000;
const TicksPerMinute = TicksPerSecond * 60;
const TicksPerHour = TicksPerMinute * 60;
const TicksPerDay = TicksPerHour * 24;

const MaxSecondsFractionDigits = 7;

const timeSpanPattern = /^([-])?(?:(\d+)\.)?(\d\d):(\d\d):(\d\d)(\.\d{1,7})?$/;

export class TimeSpan {
    private constructor(private readonly ticks: number) {
        this.ticks = ticks;
    }

    get milliseconds(): number {
        return Math.trunc((this.ticks / TicksPerMillisecond) % 1000);
    }

    get seconds(): number {
        return Math.trunc((this.ticks / TicksPerSecond) % 60);
    }

    get minutes(): number {
        return Math.trunc((this.ticks / TicksPerMinute) % 60);
    }

    get hours(): number {
        return Math.trunc((this.ticks / TicksPerHour) % 24);
    }

    get days(): number {
        return Math.trunc(this.ticks / TicksPerDay);
    }

    asMilliseconds(): number {
        return this.ticks / TicksPerMillisecond;
    }

    asSeconds(): number {
        return this.ticks / TicksPerSecond;
    }

    asMinutes(): number {
        return this.ticks / TicksPerMinute;
    }

    asHours(): number {
        return this.ticks / TicksPerHour;
    }

    asDays(): number {
        return this.ticks / TicksPerDay;
    }

    /**
     * Converts the time span to an ISO 8601 duration string, without years,
     * months, or weeks
     */
    toIsoDurationString(): string {
        return `P${this.days.toFixed(0)}D${this.hours.toFixed(0)}H${this.minutes.toFixed(
            0
        )}M${this.seconds.toPrecision(3)}S`;
    }

    /**
     * Formats duration as a .NET TimeSpan
     *
     * Pattern.Full: [-]dd.hh:mm:ss.fffffff
     * Pattern.Minimum: [-][d.]hh:mm:ss[.fffffff]
     *
     * https://docs.microsoft.com/en-us/dotnet/standard/base-types/standard-timespan-format-strings#the-constant-c-format-specifier
     */
    toDurationString(): string {
        const absTicks = Math.abs(this.ticks);
        const days = Math.floor(absTicks / TicksPerDay);
        const time = absTicks % TicksPerDay;
        const hours = Math.floor((time % TicksPerDay) / TicksPerHour);
        const minutes = Math.floor((time % TicksPerHour) / TicksPerMinute);
        const seconds = Math.floor((time % TicksPerMinute) / TicksPerSecond);
        const secondsFrac = (time % TicksPerSecond) / TicksPerSecond;

        const sign = this.ticks < 0 ? '-' : '';
        const daysPart = days > 0 ? `${days}.` : '';
        const hoursPart = hours.toFixed(0).padStart(2, '0');
        const minutesPart = minutes.toFixed(0).padStart(2, '0');
        const secondsPart = seconds.toFixed(0).padStart(2, '0');
        const timePart = `${hoursPart}:${minutesPart}:${secondsPart}`;
        const secondsFracPart =
            secondsFrac > 0
                ? secondsFrac.toFixed(MaxSecondsFractionDigits).substring(1)
                : '';

        return `${sign}${daysPart}${timePart}${secondsFracPart}`;
    }

    /**
     * @param durationStr string in format [-][dd.]hh:mm:ss[.fffffff]
     */
    static parse(durationStr: string): TimeSpan {
        const match = timeSpanPattern.exec(durationStr);

        if (!match) {
            throw new Error('Invalid time stamp format');
        }

        const [, sign, days, hours, minutes, seconds, secondsDecimal] = match;
        const signValue = sign === '-' ? -1 : 1;

        const totalMilliseconds =
            ((days ? Number(days) : 0) * TicksPerDay +
                (hours ? Number(hours) : 0) * TicksPerHour +
                (minutes ? Number(minutes) : 0) * TicksPerMinute +
                (seconds ? Number(seconds) : 0) * TicksPerSecond +
                (secondsDecimal ? Number(secondsDecimal) * TicksPerSecond : 0)) *
            signValue;

        return new TimeSpan(totalMilliseconds);
    }

    static fromMillis(milliseconds: number) {
        return new TimeSpan(milliseconds * TicksPerMillisecond);
    }

    static fromSeconds(seconds: number) {
        return new TimeSpan(seconds * TicksPerSecond);
    }

    static fromMinutes(minutes: number) {
        return new TimeSpan(minutes * TicksPerMinute);
    }

    static fromHours(hours: number) {
        return new TimeSpan(hours * TicksPerHour);
    }

    static fromDays(days: number) {
        return new TimeSpan(days * TicksPerDay);
    }
}
